import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { configSectionsInOut } from 'animations-lib';
import { StringFormatCapitalisation, StringFormatOptions, StringFormatSubstring } from '../../../../../utils/fieldconfig';
import { FieldConfigChangedControllerService } from '../../../data-fields/field-config-changed-controller.service';

export enum HtmlProcess {
  none = 'none',
  removeHtmlTags = 'removeHtmlTags',
  renderHtml = 'renderHtml',
}

@Component({
    selector: 'app-string-format',
    templateUrl: './string-format.component.html',
    styleUrls: ['./string-format.component.css', '../field-options.component.scss'],
    animations: [configSectionsInOut],
    standalone: false
})
export class StringFormatComponent implements OnInit {
  @Input() set fieldOptions(fieldOptions: StringFormatOptions | undefined) {
    this.initFieldOptions(fieldOptions);
    this.hasChanged = false;
  }

  @Output() fieldOptionsChange = new EventEmitter<StringFormatOptions>();

  readonly StringFormatSubstring = StringFormatSubstring;
  readonly StringFormatCapitalisation = StringFormatCapitalisation;
  readonly min: number = 1;
  readonly max: number = 2147483647;
  readonly userMax: number = 100;
  readonly default: number = -1;

  expanded = false;
  hasChanged = false;
  substringType: StringFormatSubstring = StringFormatSubstring.NONE;
  substringFromStart: number = this.default;
  substringFromEnd: number = this.default;
  substringRangeStart: number = this.default;
  substringRangeEnd: number = this.default;
  capitalisation: StringFormatCapitalisation = StringFormatCapitalisation.NONE;
  htmlProcess: HtmlProcess = HtmlProcess.none;
  containsHtml = false;

  protected readonly HtmlProcess = HtmlProcess;

  constructor(private configChangedControllerService: FieldConfigChangedControllerService) {}

  ngOnInit() {
    this.hasChanged = this.configChangedControllerService.get('StringFormatComponent');
  }

  initFieldOptions(fieldOptions: StringFormatOptions | undefined) {
    this.substringType = fieldOptions?.substringType ?? StringFormatSubstring.NONE;
    this.substringFromStart = fieldOptions?.substringFromStart ?? this.default;
    this.substringFromEnd = fieldOptions?.substringFromEnd ?? this.default;
    this.substringRangeStart = fieldOptions?.substringRangeStart ?? this.default;
    this.substringRangeEnd = fieldOptions?.substringRangeEnd ?? this.default;
    this.capitalisation = fieldOptions?.capitalisation ?? StringFormatCapitalisation.NONE;
    this.containsHtml = fieldOptions?.renderHtml || fieldOptions?.removeHtmlTags || false;

    if (fieldOptions?.renderHtml) {
      this.htmlProcess = HtmlProcess.renderHtml;
    } else if (fieldOptions?.removeHtmlTags) {
      this.htmlProcess = HtmlProcess.removeHtmlTags;
    } else {
      this.htmlProcess = HtmlProcess.none;
    }
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
  }

  updateHtml(event: any) {
    if (event.checked) {
      this.htmlProcess = HtmlProcess.renderHtml;
    } else {
      this.htmlProcess = HtmlProcess.none;
    }
    this.updateConfig();
  }

  updateConfig() {
    this.fieldOptionsChange.emit({
      removeHtmlTags: this.containsHtml && this.htmlProcess === HtmlProcess.removeHtmlTags,
      renderHtml: this.containsHtml && this.htmlProcess === HtmlProcess.renderHtml,
      substringType: this.substringType,
      ...this.setSubstringValues(),
      capitalisation: this.capitalisation,
    });
    this.configChangedControllerService.update('StringFormatComponent', true);
    setTimeout(() => (this.hasChanged = true), 0);
  }

  validateNumber(value: number): boolean {
    return value > this.min && value < this.max;
  }

  setSubstringValues(): {
    substringFromStart: number;
    substringFromEnd: number;
    substringRangeStart: number;
    substringRangeEnd: number;
  } {
    const defaultValues = {
      substringFromStart: this.default,
      substringFromEnd: this.default,
      substringRangeStart: this.default,
      substringRangeEnd: this.default,
    };
    switch (this.substringType) {
      case StringFormatSubstring.START:
        return {
          ...defaultValues,
          substringFromStart: this.validateNumber(this.substringFromStart) ? this.substringFromStart : this.min,
        };
      case StringFormatSubstring.END:
        return {
          ...defaultValues,
          substringFromEnd: this.validateNumber(this.substringFromEnd) ? this.substringFromEnd : this.min,
        };
      case StringFormatSubstring.RANGE:
        return {
          ...defaultValues,
          substringRangeStart: this.validateNumber(this.substringRangeStart) ? this.substringRangeStart : this.min,
          substringRangeEnd: this.validateNumber(this.substringRangeEnd) ? this.substringRangeEnd : this.userMax,
        };
      default:
        return defaultValues;
    }
  }
}
